import type { ReactNode } from 'react';
import { B, Bullets, C, H, Note, P, Pre, See, Steps, Table, Tip, To } from './parts';

/** Who a section is mainly for (everyone can read every section). */
export type GuideRole = 'PROJECT_ADMIN' | 'MEMBER' | 'VIEWER' | 'ADMIN';

export const GUIDE_ROLE_LABEL: Record<GuideRole, string> = {
  PROJECT_ADMIN: 'Project Admin',
  MEMBER: 'Project Member',
  VIEWER: 'View Only',
  ADMIN: 'Admin',
};

export interface GuideSection {
  /** Anchor: /guide#<id>. Keep stable, other pages may link to it. */
  id: string;
  title: string;
  /** One line under the title and in search results. */
  summary: string;
  /** Extra words people may search for. */
  keywords: string;
  roles: GuideRole[];
  group: 'Start' | 'Write tests' | 'Run and review' | 'Combine and automate' | 'Settings and admin' | 'Help';
  body: ReactNode;
}

const ALL: GuideRole[] = ['PROJECT_ADMIN', 'MEMBER', 'VIEWER', 'ADMIN'];
const TESTERS: GuideRole[] = ['PROJECT_ADMIN', 'MEMBER'];

/**
 * The product guide (How to use), in the order a team meets 1QA: set up, write tests, run them, read the
 * reports, combine and automate, settings. Update it with the feature: names of buttons, tabs and pages are
 * quoted as the app shows them.
 */
export const GUIDE_SECTIONS: GuideSection[] = [
  {
    id: 'overview',
    title: 'What 1QA does',
    summary: 'Write tests in plain English, run them on real browsers and Android devices, and share the reports.',
    keywords: 'introduction about no-code test automation how it works',
    roles: ALL,
    group: 'Start',
    body: (
      <>
        <P>
          1QA tests your web and mobile apps without code. You describe what a person does and what they should see, in plain English;
          1QA runs those steps on a real browser or Android device and shows a report with every step, screenshots and a video.
        </P>
        <Table
          head={['You', '1QA']}
          rows={[
            ['Write a test: open the app, click, type, check', 'Turns each step into actions on the real app with an AI model'],
            ['Press Run (or let a schedule or your CI do it)', 'Runs the tests on Chromium, Firefox, WebKit or an Android emulator'],
            ['Open the report', 'Shows each step as passed or failed, with screenshots, video and logs'],
            ['Share it', 'Sends it to a View Only colleague, or as a link anyone can open'],
          ]}
        />
        <Note>
          The first run of a test asks the AI model what to do for each step. 1QA remembers the answers, so the next runs of the same test
          replay them without asking again: they are faster, and they behave the same way every time.
        </Note>
      </>
    ),
  },
  {
    id: 'dashboard',
    title: 'The dashboard',
    summary: 'Your projects at a glance, split into Web, Mobile, Performance and Security.',
    keywords: 'dashboard home overview kpi pass rate my work all projects categories web mobile performance security findings',
    roles: TESTERS,
    group: 'Start',
    body: (
      <>
        <P>
          The dashboard shows how your apps are doing over the last 7 days, with a card for each kind of testing: <B>Web</B>,{' '}
          <B>Mobile</B> (Android and iOS), <B>Performance</B> and <B>Security</B>. Each card has its projects and tests, the runs, the
          share that passed, the failed runs, a 14-day trend and the last run.
        </P>
        <Table
          head={['You are', 'You see']}
          rows={[
            ['Project Admin', <>Every project of your organization (<B>All projects</B>), or only the runs you started and the tests you created (<B>My work</B>)</>],
            ['Project Member', 'The projects you are assigned to'],
          ]}
        />
        <P>
          What “passed” means depends on the card: tests that passed (Web, Mobile), performance tests that met their goals, or security
          scans that came back clean. The Security card also counts the open findings of the newest scan of each project. Below the cards,
          <B> Recent runs</B> has a tab per category.
        </P>
      </>
    ),
  },
  {
    id: 'roles',
    title: 'Roles: who can do what',
    summary: 'Admin, Project Admin, Project Member and View Only, and what each one sees.',
    keywords: 'permissions access rights viewer member admin owner',
    roles: ALL,
    group: 'Start',
    body: (
      <>
        <Table
          head={['Role', 'What they do']}
          rows={[
            ['Admin', 'Runs the platform only: users, inviting Project Admins, the organizations overview and the audit dashboard. Admins are not part of any organization, so they never see projects, tests or reports.'],
            ['Project Admin', 'Creates the organization and its projects, invites people, assigns them to projects, connects AI models and plugins, and can change anything in the organization.'],
            ['Project Member', 'Works in the projects they are assigned to: writes and runs tests, adds test data, uploads app builds and shares reports. Changes only what they created; copies the rest.'],
            ['View Only', 'Sees only the reports shared with them, and can comment on them privately.'],
          ]}
        />
        <Bullets>
          <li>
            Only the person who created a test, test data item, group, flow, schedule or webhook, or a Project Admin, can change or delete it.
            Everyone else can <B>Make a copy</B> of a test and edit the copy.
          </li>
          <li>A report can be deleted by the person who started the run, or a Project Admin.</li>
          <li>If you cannot find a project, you are not assigned to it yet: ask a Project Admin.</li>
        </Bullets>
      </>
    ),
  },
  {
    id: 'getting-started',
    title: 'First-time setup',
    summary: 'From an empty install to the first passing test, in order.',
    keywords: 'setup onboarding first steps quick start install begin new organization',
    roles: ['ADMIN', 'PROJECT_ADMIN'],
    group: 'Start',
    body: (
      <>
        <Steps>
          <li>
            <B>Admin:</B> invite your Project Admin under <To to="/admin/invitations">Administration › Invitations</To>. They get an
            email with a link to set their password.
          </li>
          <li>
            <B>Project Admin:</B> create your organization on <To to="/orgs">Organizations</To>. Each Project Admin creates one.
          </li>
          <li>
            Connect an AI model on <To to="/ai-models">AI models</To> (see <See id="ai-models">AI models</See>). Steps that act or check
            need one; open and wait steps do not.
          </li>
          <li>
            Create a project on <To to="/projects">Test projects</To> › <B>New project</B>: a web app (its address) or an Android app (its
            package name). See <See id="projects">Projects</See>.
          </li>
          <li>
            Invite your team on <To to="/members">Members</To> and pick their projects (see <See id="team">Team and invitations</See>).
          </li>
          <li>
            Write a first test (<See id="writing-tests">Writing tests</See> or <See id="recorder">Recording</See>) and press <B>Run</B>.
          </li>
        </Steps>
      </>
    ),
  },
  {
    id: 'projects',
    title: 'Projects',
    summary: 'One project per app under test: web or Android, with its own tests, data, runs and settings.',
    keywords: 'new project create web mobile android address url package settings browser video trace retries tabs',
    roles: TESTERS,
    group: 'Write tests',
    body: (
      <>
        <P>
          A project holds everything about one app: its tests, groups, flows, schedules, runs, test data, team and settings. Projects are
          sorted by kind on <To to="/projects">Test projects</To> and in the sidebar: <B>Web</B> and <B>Mobile</B> today; Performance, API,
          Security and LLM testing are coming.
        </P>
        <H>Create a project (Project Admins)</H>
        <Steps>
          <li>
            Go to <To to="/projects">Test projects</To> › <B>New project</B> and choose the kind of app.
          </li>
          <li>
            <B>Web:</B> enter the address of the app, starting with <C>https://</C>. Plain <C>http://</C> only works for <C>localhost</C>.
          </li>
          <li>
            <B>Android:</B> enter the app's package name, for example <C>com.example.shop</C>. Then upload a build (see{' '}
            <See id="mobile">Mobile apps</See>).
          </li>
        </Steps>
        <H>Project tabs</H>
        <Table
          head={['Tab', 'What it is for']}
          rows={[
            ['Tests', 'The project’s tests: create, run, copy. Opens on the tests you wrote; Everyone shows all.'],
            ['Groups / Flows', 'Run several tests together, or as a diagram with decisions (see Groups and Flows).'],
            ['Automation', 'Schedules and webhooks that start groups or flows.'],
            ['Runs', 'Every run of this project, newest first.'],
            ['Builds', 'Android projects: the app builds (APKs) runs install.'],
            ['Test data', 'Values tests use, and environments (collections).'],
            ['Team', 'Who is assigned to the project (Project Admins assign).'],
            ['Settings', 'Browser, video and trace recording, and how often a failing step is retried.'],
          ]}
        />
        <H>Settings worth knowing</H>
        <Bullets>
          <li>
            <B>Browser:</B> Chromium, Firefox or WebKit (Safari’s engine).
          </li>
          <li>
            <B>Video</B> and <B>trace</B> (a step-by-step page with screenshots): always, or only for failed tests to save space.
          </li>
          <li>
            <B>Retry failed steps:</B> a step that fails is tried again in place (default twice) before the test fails, which absorbs slow
            pages. Set it to Off to fail at once.
          </li>
        </Bullets>
      </>
    ),
  },
  {
    id: 'writing-tests',
    title: 'Writing tests in the builder',
    summary: 'Open, act, check and wait: four kinds of steps, written as you would explain them to a colleague.',
    keywords: 'test builder prompt automation steps open act assert check wait plain english new test edit copy name',
    roles: TESTERS,
    group: 'Write tests',
    body: (
      <>
        <P>
          In a project, choose <B>New test</B> › <B>Prompt automation</B> to open the builder (or <B>Record in the browser</B>, see{' '}
          <See id="recorder">Recording</See>). A test is a name and a list of steps, run from top to bottom.
        </P>
        <Table
          head={['Step', 'What it does', 'Example']}
          rows={[
            ['Open', 'Web: opens a page of the app (a path or address). Android: launches the app.', <C key="o">/login</C>],
            ['Act', 'Does one thing a person would do: click, type, choose, scroll.', <C key="a">click the "Sign in" button</C>],
            ['Check', 'States what must be true on the screen now. The test fails if it is not.', <C key="c">the page shows "Welcome back, Dana"</C>],
            ['Wait', 'Pauses, in milliseconds (at most 30 000).', <C key="w">2000</C>],
          ]}
        />
        <H>Write steps that run the same way every time</H>
        <Bullets>
          <li>One action per Act step. “Type the email, then click Sign in” is two steps.</li>
          <li>
            Name controls by the text people see, in quotes: <C>type "demo@1qa.io" into the Email field</C>.
          </li>
          <li>
            Checks describe what is visible, not how the app works: <C>the cart shows 2 items</C>.
          </li>
          <li>Each test should start from its own Open step and not rely on what another test did before it.</li>
          <li>
            Sign-ups and other “only once” data: use a fresh value such as <C>{'{{$randomEmail}}'}</C> (see{' '}
            <See id="test-data">Test data</See>).
          </li>
        </Bullets>
        <Tip>
          Use the ready-made step templates in the builder to start, and the <B>Run</B> button there to try a test while you write it. Test
          names are unique per project; the builder warns you before saving a duplicate.
        </Tip>
        <Note title="Tests written by others">
          You can open and run any test in your projects. To change one someone else wrote, use <B>Make a copy</B> and edit the copy (Project
          Admins can edit any test).
        </Note>
      </>
    ),
  },
  {
    id: 'from-documents',
    title: 'Tests from business documents',
    summary: 'Turn a BRD, user stories or a specification into reviewed tests: requirements first, then test cases.',
    keywords: 'brd business requirements document specification user stories acceptance criteria pdf word docx upload ai generate test cases coverage traceability',
    roles: TESTERS,
    group: 'Write tests',
    body: (
      <>
        <P>
          In a web or mobile project, <B>New test</B> › <B>From business documents</B> turns what the business team wrote into tests. The
          project’s AI model does the reading and the first draft; you decide what is saved.
        </P>
        <Steps>
          <li>
            <B>Documents.</B> Upload up to 5 files (PDF, Word .docx, text or Markdown, 15 MB each) or paste user stories. Add notes for the
            AI model if it helps: roles and test accounts, parts to leave out, words your business uses. Scanned pages and images are not read.
          </li>
          <li>
            <B>Requirements.</B> The AI model lists each testable requirement (REQ-1, REQ-2…) with acceptance criteria, priority and where it is
            in the document, plus <B>questions for the business team</B> where the document is unclear. Edit, add or remove requirements and
            tick the ones that need tests.
          </li>
          <li>
            <B>Test cases.</B> For the ticked requirements it drafts positive, negative and edge-case tests in 1QA’s steps. Read each one, fix
            steps, then <B>Accept</B> or <B>Reject</B> it. The <B>Coverage</B> list shows which requirements still have no accepted test;{' '}
            <B>Write again</B> redrafts one requirement.
          </li>
          <li>
            <B>Save.</B> Accepted tests become ordinary tests, tagged <C>from-requirements</C>, their kind and their requirement keys, and
            optionally put in a new group so you can run them together.
          </li>
        </Steps>
        <Note title="Test data">
          The drafts use your test data names (<C>{'{{EMAIL}}'}</C>) and suggest new ones with an example value (<C>{'{{COUPON|SAVE10}}'}</C>),
          which work right away. Passwords and other secrets never get a value: add them as secret test data first, otherwise those tests are
          left out when you save (the page lists them).
        </Note>
        <Bullets>
          <li>The documents’ text is stored encrypted and only sent to the project’s AI model. Deleting the upload keeps the tests saved from it.</li>
          <li>Long documents are read in parts; this can take a few minutes. You can leave the page and come back.</li>
          <li>Only the person who uploaded the documents, or a Project Admin, changes them; others can look.</li>
        </Bullets>
      </>
    ),
  },
  {
    id: 'recorder',
    title: 'Recording a test in the browser',
    summary: 'Install the 1QA Recorder for Chrome or Edge, click through your app, and save the steps as a test.',
    keywords: 'recorder extension chrome edge record clicks typing install connect plugin capture performance load requests',
    roles: TESTERS,
    group: 'Write tests',
    body: (
      <>
        <H>Install it once</H>
        <Steps>
          <li>
            Open <To to="/helpers/recorder">Helper services › Browser recorder</To> and download the recorder. Unzip it into a folder you will keep.
          </li>
          <li>
            In Chrome open <C>chrome://extensions</C> (Edge: <C>edge://extensions</C>), turn on <B>Developer mode</B>, click{' '}
            <B>Load unpacked</B> and choose the unzipped folder. Pin <B>1QA Recorder</B> to the toolbar.
          </li>
          <li>
            Click the recorder icon, paste the 1QA address shown on the Browser recorder page and press <B>Connect</B>. You are already
            signed in, so no extra sign-in is needed.
          </li>
        </Steps>
        <H>Record</H>
        <Steps>
          <li>
            In a project choose <B>New test</B> › <B>Record in the browser</B>. A fresh window opens on your app.
          </li>
          <li>Use the app as a person would. Clicks, typing, choices and ticks become steps.</li>
          <li>
            To add a check, select text on the page, right-click and choose <B>1QA: Check this text is shown</B>.
          </li>
          <li>
            Press <B>Stop</B>, review the steps, give the test a name and save it to the project. It appears in the project’s Tests list at
            once.
          </li>
        </Steps>
        <H>Record a performance script</H>
        <P>
          The recorder has two modes, chosen at the top of its window: <B>Functional test</B> (steps for a web project) and{' '}
          <B>Performance script</B> (the requests your app sends, for a performance project). In a performance project,{' '}
          <B>Scripts</B> › <B>New script</B> starts the performance mode by itself (recorder 0.6.2 or newer). Chrome shows a “started
          debugging this browser” bar while recording. When you save, the script goes straight into the
          performance project’s <B>Scripts</B>, each request named after the click that caused it; typed passwords become secret test
          data of that project. See <See id="performance">Performance tests</See>.
        </P>
        <Note>
          Passwords you type are not written into the steps: they are saved as test data (<C>PASSWORD</C>, <C>PASSWORD_2</C>…) and the step
          uses <C>{'{{PASSWORD}}'}</C>. If 1QA says the recorder is missing or too old, reload the page after installing, or download the
          latest version from the Browser recorder page.
        </Note>
      </>
    ),
  },
  {
    id: 'test-data',
    title: 'Test data and variables',
    summary: 'Keep emails, passwords and other values out of the steps with {{NAME}}, and use built-in fresh values.',
    keywords: 'variables placeholders secrets password test data values built-in random email uuid date written value',
    roles: TESTERS,
    group: 'Write tests',
    body: (
      <>
        <P>
          Write <C>{'{{NAME}}'}</C> in a step and 1QA fills in the value when the test runs, for example{' '}
          <C>{'type "{{EMAIL}}" into the Email field'}</C>. Values live on <To to="/test-data">Test data</To> (sidebar) or a project’s{' '}
          <B>Test data</B> tab: for the whole organization or for one project. A project value wins over an organization value with the
          same name.
        </P>
        <H>Secret values</H>
        <P>
          Mark passwords and tokens as <B>secret</B>. A secret is never shown again, never sent to the AI model, and is hidden in logs,
          traces and reports. Secrets work in Act steps only and need at least 6 characters.
        </P>
        <H>Built-in values</H>
        <Table
          head={['Write', 'You get']}
          rows={[
            [<C key="1">{'{{$randomEmail}}'}</C>, 'A unique email address each run, for sign-ups'],
            [<C key="2">{'{{$uuid}}'}</C>, 'A random unique id'],
            [<C key="3">{'{{$randomNumber}}'}</C>, 'A random 6-digit number'],
            [<C key="4">{'{{$date}}'}</C>, 'Today’s date, e.g. 2026-10-22'],
            [<C key="5">{'{{$timestamp}}'}</C>, 'The current time in milliseconds'],
          ]}
        />
        <H>A fallback written in the step</H>
        <P>
          <C>{'{{CITY|Berlin}}'}</C> uses the test data value CITY when there is one, and “Berlin” when there is not. The report says
          which steps used the written value. Not for secrets.
        </P>
      </>
    ),
  },
  {
    id: 'collections',
    title: 'Environments (test data collections)',
    summary: 'Run the same tests against Dev, Staging or Preprod by switching one set of values.',
    keywords: 'collections environments dev staging preprod app url switch values per environment',
    roles: TESTERS,
    group: 'Write tests',
    body: (
      <>
        <P>
          A collection is a named set of test data, usually one per environment. Values in the chosen collection replace the shared values
          with the same name; everything else keeps its shared value.
        </P>
        <Steps>
          <li>
            On <B>Test data</B>, create a collection (for the organization or one project), e.g. <B>Staging</B>.
          </li>
          <li>Add the values that differ there: the Staging user, its password…</li>
          <li>
            Add <C>APP_URL</C> to point web tests at that environment’s address instead of the project’s.
          </li>
          <li>
            Choose it in the <B>Test data</B> picker next to any Run button. The choice is remembered per project.
          </li>
        </Steps>
        <Tip>
          The Test data page lists names your tests use that the chosen collection cannot fill. Click a name to add it. Schedules and
          webhooks can use a collection too.
        </Tip>
      </>
    ),
  },
  {
    id: 'running',
    title: 'Running tests',
    summary: 'Run one test, a selection, or all of them; watch it live; pick test data and builds.',
    keywords: 'run start execute watch live view stream screen emulator device cancel queue select tests run all progress',
    roles: TESTERS,
    group: 'Run and review',
    body: (
      <>
        <Bullets>
          <li>
            <B>One test:</B> <B>Run</B> in the builder, or the run button on its row in the project’s Tests tab.
          </li>
          <li>
            <B>Several:</B> tick tests and press <B>Run</B>, or choose <B>Everyone</B> and <B>Run all tests</B>.
          </li>
          <li>
            <B>Groups and flows</B> have their own run buttons (see <See id="groups">Groups</See> and <See id="flows">Flows</See>).
          </li>
        </Bullets>
        <P>Next to the run buttons you can choose:</P>
        <Bullets>
          <li>
            <B>Test data:</B> which environment to run against (see <See id="collections">Environments</See>).
          </li>
          <li>
            <B>Build</B> (Android): which app build to install; Latest by default (see <See id="mobile">Mobile apps</See>).
          </li>
          <li>
            <B>Device</B> (Android): which kind of device to run on, e.g. Pixel 9 · Android 16; Any device by default.
          </li>
          <li>
            <B>Watch live</B> (web projects on Chromium, and Android projects): see the browser or the device’s screen on the report page
            while the test runs. On the web, the picture pauses while a secret value is readable; Android tests that type secret test data
            are not shown at all.
          </li>
        </Bullets>
        <P>
          The run page updates by itself and shows the step that is running. You can cancel a run while it is queued or running.
        </P>
      </>
    ),
  },
  {
    id: 'reports',
    title: 'Reading and sharing reports',
    summary: 'What Passed, Failed and Error mean, where the evidence is, and how to share a report.',
    keywords: 'report results passed failed error skipped not run screenshots video trace log flaky share link viewer comment delete',
    roles: ALL,
    group: 'Run and review',
    body: (
      <>
        <P>
          Every run has a report: <To to="/reports">Reports</To> lists them (yours first, <B>Everyone</B> for all). Click a test in a
          report to see its steps and evidence.
        </P>
        <Table
          head={['Result', 'Meaning', 'What to do']}
          rows={[
            ['Passed', 'Every step did what the test says.', '—'],
            ['Failed', 'The app did not behave as the test expects: a check was false, or something could not be done.', 'Look at the step marked failed, its screenshot and the video.'],
            ['Error', 'The test could not run properly, for a reason outside the app: missing test data, no AI model, no device…', 'Read “What went wrong”; it says what to fix.'],
            ['Not run', 'The test was skipped, e.g. a group stopped at the first failure.', '—'],
          ]}
        />
        <H>Evidence for each test</H>
        <Bullets>
          <li>Steps with their status, and which test data values came from where.</li>
          <li>Screenshots and the video of the test, and the trace: a page with every step, what it did and the screen at the failure.</li>
          <li>Android tests: the device it ran on and the app’s log (<C>device-log.txt</C>).</li>
          <li>The technical log, for when you need every detail.</li>
          <li>“This test passed last time” when a failure may be flaky, with the usual causes.</li>
        </Bullets>
        <H>Share a report</H>
        <Steps>
          <li>
            On a run’s report, choose <B>Share</B>.
          </li>
          <li>Share it with a person in your organization (usually a View Only user) by email, or create a link anyone can open, with an expiry date.</li>
          <li>
            A View Only user can comment on a report shared with them; the comment goes privately to the person who shared it. New comments
            show as a number next to <B>Reports</B>.
          </li>
        </Steps>
      </>
    ),
  },
  {
    id: 'groups',
    title: 'Groups: run tests together',
    summary: 'An ordered set of tests, run one after another or several at the same time.',
    keywords: 'group suite smoke regression parallel sequential stop at first failure together batch',
    roles: TESTERS,
    group: 'Combine and automate',
    body: (
      <>
        <Steps>
          <li>
            In a project open <B>Groups</B> › <B>Create a group</B>, name it (e.g. “Smoke check”) and pick tests in the order they should run.
          </li>
          <li>
            Choose <B>one after another</B>, or <B>at the same time</B> with how many at once (2 to 8).
          </li>
          <li>
            Optionally <B>stop at the first failure</B>: tests that did not start yet are reported as Not run.
          </li>
          <li>
            Press <B>Run group</B>. The report lists the results in the group’s order.
          </li>
        </Steps>
        <Note>Groups refer to the tests, so editing a test changes it in every group that uses it.</Note>
      </>
    ),
  },
  {
    id: 'flows',
    title: 'Flows: tests with decisions',
    summary: 'A diagram of tests and groups, with branches that depend on the results.',
    keywords: 'flow diagram condition branch join notify notification slack teams email end start pipeline workflow decision pass rate',
    roles: TESTERS,
    group: 'Combine and automate',
    body: (
      <>
        <P>
          A flow runs tests and groups in a diagram, for example “run the smoke group; if everything passed, run the full checkout group,
          otherwise stop”. Open a project’s <B>Flows</B> tab and create one.
        </P>
        <Table
          head={['Step', 'Shape', 'What it does']}
          rows={[
            ['Start', 'Pill', 'Where the flow begins.'],
            ['Test', 'Box', 'Runs one test; it becomes its own report.'],
            ['Group', 'Stack of boxes', 'Runs a test group; it becomes its own report.'],
            ['Condition', 'Diamond', 'Asks a question and continues on Yes (left corner) or No (right corner): did everything pass, did anything fail, is the pass rate at least…, did a specific test fail — about the step just before or everything so far.'],
            ['Join', 'Circle', 'Waits for branches to meet: all of them, or the first one.'],
            ['Notify', 'Pointed on the left, rounded on the right', 'Sends a message to Slack, Microsoft Teams, Zoho Cliq or email: the results so far, your own text, or both. Choose the channels, the email recipients and when to send (always, only if something failed, only if all passed). It also runs after a failure, and a message that cannot be delivered never fails the flow.'],
            ['End', 'Octagon (stop sign)', 'Where a branch finishes.'],
          ]}
        />
        <P>
          Connect steps by dragging between them. The editor checks the flow as you build it and says what is missing. On a flow run, each
          step is coloured by its result and links to its report. Branches run side by side. A Notify step only offers the channels a Project
          Admin has connected on the Plugins page.
        </P>
      </>
    ),
  },
  {
    id: 'automation',
    title: 'Schedules and webhooks',
    summary: 'Run a group or flow every night, or from your CI pipeline after each deploy.',
    keywords: 'schedule cron nightly timer webhook ci gitlab pipeline deploy automation trigger token status',
    roles: TESTERS,
    group: 'Combine and automate',
    body: (
      <>
        <P>
          In a project open <B>Automation</B> and add a <B>schedule</B> or a <B>webhook</B>. Each one starts a group or a flow, optionally
          with a test data collection, and runs as the person who created it.
        </P>
        <H>Schedules</H>
        <P>
          Every day, Monday to Friday, once a week, every hour, or a custom cron expression, in your time zone. At most every 10 minutes. A
          schedule waits while its previous run is still going.
        </P>
        <H>Webhooks (CI)</H>
        <Steps>
          <li>Create a webhook. Copy its URL and secret token: the token is shown only once (you can make a new one later).</li>
          <li>Call it from your pipeline after a deploy. GitLab webhooks work as they are; anything else can use a POST request:</li>
        </Steps>
        <Pre>{`curl -X POST "https://<your 1QA address>/api/v1/hooks/<webhook id>?collection=Staging" \\
  -H "X-1QA-Token: <secret token>"`}</Pre>
        <P>
          The answer includes a <C>reportUrl</C> and a <C>statusUrl</C>; your pipeline can poll the status URL (with the same token) to
          wait for the result. <C>?collection=</C> is optional.
        </P>
        <H>Notifications</H>
        <P>
          Each schedule and webhook can notify when a run fails and when it passes again (the default), after every run, or never. Where
          notifications go is set up under <See id="notifications">Notifications</See>.
        </P>
      </>
    ),
  },
  {
    id: 'notifications',
    title: 'Notifications: Slack, Teams, Zoho Cliq and email',
    summary: 'Tell your team when an automated run fails, and when it is fixed.',
    keywords: 'notifications slack microsoft teams zoho cliq email smtp plugins alerts messages',
    roles: ['PROJECT_ADMIN'],
    group: 'Combine and automate',
    body: (
      <>
        <Steps>
          <li>
            On <To to="/plugins">Plugins</To>, add Slack, Microsoft Teams or Zoho Cliq with an incoming webhook URL from that tool, or email
            with your SMTP server. Add it for the whole organization or for one project.
          </li>
          <li>
            Press <B>Send test</B> to check a message arrives.
          </li>
          <li>
            Choose when each schedule or webhook notifies (see <See id="automation">Schedules and webhooks</See>). Messages link to the
            report.
          </li>
        </Steps>
        <Note>Notifications are sent for runs started by schedules and webhooks, not for runs started by hand.</Note>
      </>
    ),
  },
  {
    id: 'ai-models',
    title: 'AI models',
    summary: 'Connect the model that turns plain-English steps into actions.',
    keywords: 'ai model llm openai anthropic claude bedrock litellm gemini api key provider default',
    roles: ['PROJECT_ADMIN'],
    group: 'Settings and admin',
    body: (
      <>
        <Steps>
          <li>
            Go to <To to="/ai-models">AI models</To> and add a model: OpenAI, Anthropic, Amazon Bedrock, LiteLLM or Google Gemini.
          </li>
          <li>Enter its key or connection details and test it.</li>
          <li>Make it the default for the organization, or add one for a single project: a project’s own default wins.</li>
        </Steps>
        <Note>
          Keys are stored encrypted and never shown again; enter a new key to change it. Tests with only Open and Wait steps run without a
          model; a test that needs one and finds none stops with an Error that says so.
        </Note>
      </>
    ),
  },
  {
    id: 'mobile',
    title: 'Mobile apps (Android)',
    summary: 'Upload an APK, run tests on emulators, and get the device and its log in the report.',
    keywords: 'mobile android apk app build upload emulator device phone tablet pixel version sdk api 36 android 16 select choose package logcat ios runner',
    roles: TESTERS,
    group: 'Settings and admin',
    body: (
      <>
        <Steps>
          <li>
            Create an Android project with the app’s package name (see <See id="projects">Projects</See>).
          </li>
          <li>
            Open its <B>Builds</B> tab and upload the APK (drag and drop works). 1QA reads its version and refuses an APK of another app.
            Add notes like “Release 2.4 RC1” if you like.
          </li>
          <li>
            Write tests as usual. <B>Open</B> launches the app; steps describe what to tap and type.
          </li>
          <li>
            Press <B>Run</B>. The <B>Build</B> picker next to it chooses the build: <B>Latest</B> unless you pick one. Schedules and webhooks
            use the latest build.
          </li>
          <li>
            To test on a particular phone or Android version, choose it in the <B>Device</B> picker, e.g. <B>Pixel 9 · Android 16</B> or{' '}
            <B>Pixel Tablet · Android 16</B>. It lists the devices your Android runner offers and how many are ready; <B>Any device</B> uses
            whichever is free first.
          </li>
        </Steps>
        <Bullets>
          <li>Each run installs the chosen build first, and every test starts with the app’s data cleared, so no sign-in leaks from the test before.</li>
          <li>Tests of a group that run at the same time each get their own emulator.</li>
          <li>
            To test on a real phone, connect it with the Device Agent: see <See id="device-agent">Connecting your own phone</See>.
          </li>
          <li>The report shows the device (e.g. “Emulator · Android 14”) and attaches the app’s log for each test.</li>
        </Bullets>
        <Note title="If runs wait">
          “No Android runner is connected” or “No Android device is ready yet” on the project page means the Android runner is not running
          or its emulators are still starting (a minute or two). Runs start by themselves once a device is ready; ask your administrator if
          the message stays.
        </Note>
        <Tip>
          Inside the emulator, <C>localhost</C> is the emulator itself. To reach a server on the machine that runs 1QA, use{' '}
          <C>10.0.2.2</C>. Upload an <C>.apk</C>; an Android App Bundle (<C>.aab</C>) cannot be installed directly. iPhone apps are not
          supported yet.
        </Tip>
      </>
    ),
  },
  {
    id: 'phone-recorder',
    title: 'Recording a test on your phone',
    summary: 'Use your Android app on a real phone; 1QA turns your taps, typing and scrolling into steps you review and save.',
    keywords: 'record recorder mobile phone android tap swipe scroll type typing long press back check device agent capture steps automatic',
    roles: TESTERS,
    group: 'Write tests',
    body: (
      <>
        <P>
          In an Android project, <B>New test › Record on your phone</B> records a test while you use the app on a real phone. The phone has
          to be connected through the Device Agent, version 1.2.2 or newer (see <See id="device-agent">Connecting your own phone</See>).
        </P>
        <Steps>
          <li>
            Choose the phone and the app: a build to install, or the app already on the phone. Press <B>Start recording</B>. The app
            opens with its data cleared, like at the start of every test.
          </li>
          <li>
            Use the app on the phone. Taps, long presses, typing, scrolling, swipes and Back become steps, which show up in 1QA as you go.
            Give each screen a moment to load, so 1QA can read it.
          </li>
          <li>
            To check that the app shows something, press <B>Add a check</B> and pick the text from the screen (or type it).
          </li>
          <li>
            Press <B>Stop</B>. Review the steps: change their wording, remove the ones you don't need, and give the test a name. Then{' '}
            <B>Save test</B>. It opens in the builder, ready to run.
          </li>
        </Steps>
        <Bullets>
          <li>
            Phones never show what is typed into password fields. The step becomes <C>{'type {{PASSWORD}} into …'}</C>, and the review
            asks for the value once. It is saved as test data in the project, where you can mark it secret.
          </li>
          <li>
            Some apps give icons and buttons no name a phone can read. For those taps 1QA takes a screenshot of the phone the moment
            your finger lands, and the project&apos;s AI model names what you tapped, for example “tap the &quot;Home&quot; icon in
            the bottom navigation bar”. The review shows the screenshot (the red ring marks your tap) beside the step. This needs an
            AI model in the project or organization and Android 11 or newer.
          </li>
          <li>
            Steps with a warning sign need a look: the AI model named them, it could not, or you were faster than 1QA could read the
            screen. Check them, or describe them in your own words, for example “tap the cart icon”.
          </li>
          <li>
            The Enter key on the on-screen keyboard is not recorded. Add “press Enter” in the review if your app needs it. Pinch and
            zoom are not recorded either.
          </li>
          <li>
            The first time, the agent installs a small helper app on the phone, the <B>1QA Recorder Helper</B>. It tells 1QA which
            element you touch and what you type, but never passwords. It is switched on only while you record, and your phone's
            accessibility settings are put back afterwards. Some phones (for example Xiaomi) need <B>Install via USB</B> switched on
            in the developer options first.
          </li>
          <li>
            If some steps still have no name, send the recording's details file to your 1QA administrator. It lists what the phone
            reported for each touch, but never what you typed. The agent window prints its full path when the recording starts. The
            files are in the <C>.1qa-device-agent\recordings</C> folder in your user folder: on Windows type{' '}
            <C>%USERPROFILE%\.1qa-device-agent\recordings</C> into the File Explorer address bar; on a Mac press Cmd+Shift+. in
            Finder to show hidden folders.
          </li>
          <li>While you record, the phone is busy: tests wait for it. Leaving the page cancels the recording.</li>
        </Bullets>
      </>
    ),
  },
  {
    id: 'phone-performance-recorder',
    title: 'Recording a performance script on an emulator or your phone',
    summary: 'Use your Android app on one of 1QA’s emulators (in the browser) or a real phone; 1QA records the requests it sends and saves them as a performance script.',
    keywords: 'performance script load mobile phone emulator android flutter record traffic network requests proxy certificate ca https device agent transactions browser screen',
    roles: TESTERS,
    group: 'Write tests',
    body: (
      <>
        <P>
          The phone recorder can also record what your app sends over the network. Open it from a performance project (
          <B>Scripts › New script › Record on an emulator or phone</B>, then choose the app), or from the app&apos;s Android project (
          <B>New test › Record on your phone</B>) with <B>What to record: Performance script</B>.
        </P>
        <H>On a 1QA emulator (nothing to set up)</H>
        <P>
          Choose a <B>1QA emulator</B> under <B>Record on</B>, and the app build to install (upload it under the Android project&apos;s{' '}
          <B>Builds</B> first). Press <B>Start recording</B>: the emulator&apos;s screen shows up in 1QA. Click to tap, drag to scroll, type into the box
          under the screen, and use <B>Back</B> and <B>Home</B> below it. Nothing is installed on a phone and nothing changes in the app: any build
          works, Flutter apps included, and only your app&apos;s own requests are recorded. Large debug builds can take a minute or two to show their
          first screen. The emulator starts afresh after each recording. Emulators need the Android runner (ask your 1QA administrator).
        </P>
        <H>On your phone</H>
        <P>
          You need the Device Agent 1.4.0 or newer (see <See id="device-agent">Connecting your own phone</See>).
        </P>
        <Steps>
          <li>Choose the phone, the app and the performance project the script goes to. Its app address is your app&apos;s API address.</li>
          <li>
            If the performance project has your API&apos;s own certificate (see below), there is nothing to install: skip to the next step. Otherwise, the
            first time, install the 1QA certificate on the phone. When you press Start, the agent copies it to the phone as{' '}
            <C>Download/1QA-recorder-CA.crt</C>. Open Settings › Security › Encryption &amp; credentials › Install a certificate › CA
            certificate and pick that file (the names differ a little between phones). Each computer with a Device Agent has its own
            certificate.
          </li>
          <li>Use the app on the phone. The steps and the number of recorded requests show up in 1QA as you go.</li>
          <li>
            Press <B>Stop</B>. Each step names the requests sent after it (a transaction in the script), so give the steps clear names.
            Type the passwords you entered, give the script a name and press <B>Save script</B>. 1QA finds the values that change per
            session, as for browser recordings. If it needs you to check something first, the script&apos;s review opens.
          </li>
        </Steps>
        <Bullets>
          <li>
            <B>No setup on phones:</B> a Project Admin adds the certificate and private key of your test environment&apos;s API under the performance
            project&apos;s <B>Settings › Server certificates</B> (PEM; the server certificate first, then its intermediates). Phones recording into that
            project then need nothing installed, and any build of the app works, release builds and pinned apps included. Use test environments only:
            the key goes to the Device Agent of whoever records.
          </li>
          <li>
            Without a server certificate, the app must trust certificates the user installed. Debug and test builds do when their{' '}
            <C>network_security_config</C> has <C>{'<certificates src="user" />'}</C> in its trust anchors. Release builds and apps that
            pin their certificates send their requests past 1QA: the recorder names those hosts, and the app keeps working.
          </li>
          <li>Apps built with Flutter ignore the phone&apos;s proxy setting, so their requests cannot be recorded on a phone. Record them on a 1QA emulator instead.</li>
          <li>
            While recording, the phone&apos;s internet goes through the agent&apos;s computer. 1QA switches that off when you stop, cancel, or
            close the agent; if the agent was closed by force, it switches it off as soon as it sees the phone again.
          </li>
          <li>Uploaded photos and files are not kept in the recording: upload them under Scripts › Test files, as for browser recordings.</li>
        </Bullets>
      </>
    ),
  },
  {
    id: 'performance',
    title: 'Performance tests: put your app under load',
    summary: 'Make scripts of user journeys, combine them into a load plan (scenarios, ramp-up, steady load, goals), run it on load agents and read the verdict.',
    keywords: 'performance load stress spike soak test users concurrent requests per second per minute arrival rate ramp up ramp down steady duration error 401 403 404 429 500 502 503 504 timeout connection refused suggestions findings scenario mix share distribution csv excel xlsx data file test data file rows credentials users accounts template iteration loop stop used up skip skipped duplicate repeated preflight options retry split equally percentage maximum users vus virtual users script library pacing think time response time p95 p99 har recording recorder load agent k6 capacity correlation slo goal upload image photo picture file multipart form-data attachment',
    roles: TESTERS,
    group: 'Run and review',
    body: (
      <>
        <P>
          A performance test puts many users on your app at once and measures how fast it answers. It has two parts: <B>scripts</B>, each
          one user’s journey (sign in and buy, browse the catalogue…), and the <B>test</B> itself, a load plan that says which scripts
          run, how many users each gets and how the load builds up. Tests run on <B>load agents</B> (machines you connect).
        </P>
        <P>
          Performance testing has projects of its own, apart from web and mobile automation: on <To to="/projects">Test projects</To>{' '}
          press <B>New project</B> › <B>Performance</B> and give the address of the app to load (an internal test server is fine). The
          project has its performance tests, scripts, runs, test data and load limits.
        </P>
        <H>1. Connect a load agent (once)</H>
        <P>
          On <To to="/helpers/load-agents">Helper services › Load agents</To>, press <B>Connect a load agent</B>, download the agent
          onto a spare machine with Node.js 22 and start it with the command shown (it contains a one-time token). The agent measures
          its machine for about 40 seconds, so 1QA can tell you how many users it can carry. It only connects out to 1QA.
        </P>
        <H>2. Make scripts</H>
        <P>
          In the performance project, open <B>Scripts</B> › <B>New script</B>: the 1QA Recorder opens your app in a new window in its{' '}
          <B>Performance script</B> mode. Click through the journey, press <B>Stop</B>, name the script and save it to this project; it
          appears under <B>Scripts</B> right away. Or upload HAR files from DevTools, Charles or Fiddler.
        </P>
        <P>
          1QA keeps the app’s requests, finds every value that changes per session (sign-in tokens, ids, CSRF fields) and makes a rule
          for it, and turns the test data typed into references, so secrets stay secret. Each step becomes a transaction named after it.
          A recorded script is saved to the project’s library at once (open it there to check it); a HAR file is reviewed first. If 1QA
          finds something to fix in a recording (for example a host outside the load limits), the recorder sends you to that review
          instead. A script can be used by many tests.
        </P>
        <P>
          A load test sends your app’s <B>backend</B> calls, not what the browser’s front end loads for itself. When you save a recording,
          keep <B>Backend API requests only</B> for apps built with React, Angular, Vue or Next.js; choose <B>Pages and API</B> only for apps
          that render their pages on the server. A script that still holds front-end requests (pages, Next.js <C>_rsc</C> requests) shows
          them above its requests with <B>Remove front-end requests</B>. 1QA also leaves out what the browser or the app adds by
          itself: <B>CORS preflights</B> (<C>OPTIONS</C>), a request that <B>failed while you recorded and that the app sent
          again</B> (for example a logout that got 401, a token refresh, and the logout again) and the <B>same request sent twice at
          once</B>. The recorder and the review say how many were left out.
        </P>
        <P>
          Open a request and press <B>Skip</B> to keep it in the script without sending it (it shows crossed out; <B>Turn on</B> sends
          it again). Use it to try a script without a request before deleting it. If a skipped request takes a value that later
          requests use, the editor warns you.
        </P>
        <P>
          Open a script to see its requests step by step and change them (if you made it, or as a Project Admin; others make a copy).
          Open a request to edit its address, headers and body. To <B>parameterize</B>, select a value (a user name, a product id) and
          press <B>Make selection test data</B>: it becomes <C>{'{{NAME}}'}</C> here or everywhere it appears, and the recorded value can be
          saved as the project’s test data. For <B>correlation</B>, the request’s <B>Correlation</B> tab takes a value from its response
          (JSON path, header, cookie, HTML field or a text pattern); paste the value it had when recorded and press{' '}
          <B>Use in later requests</B>. The panel above the requests lists every value, warns when test data is missing or a value is used
          before a response gives it. Press <B>Save changes</B> when done.
        </P>
        <P>
          Before putting a script in a performance test, press <B>Check the script (1 user)</B> on its page: one user goes through the
          journey once on a load agent, with the project’s test data (pick a collection next to the button), and every request shows
          whether it answered as recorded and, if not, why. <B>Details</B> on each request shows the request as sent (address,
          headers, cookies, body) and the response (status, time, headers, body), and the values taken from it; secret test data
          shows as ••••••. Fix what failed, save, and check again. Checks stay on the script’s page;
          they are not runs or reports.
        </P>
        <H>3. Plan the load</H>
        <Bullets>
          <li>
            <B>Scenarios</B>: each is a script with its part of the users, an optional start delay, the pause between pages (as
            recorded, none, or between two times) and <B>pacing</B> (a user starts a new journey at most every N seconds).{' '}
            <B>Split the users</B> between the scenarios <B>equally</B>, <B>by percentage</B> (70 % buyers, 30 % browsers) or{' '}
            <B>by number of users</B> (70 buyers, 30 browsers: the maximum users becomes 100). Each scenario shows how many users it
            gets; they always add up to the maximum.
          </li>
          <li>
            <B>Load strategy</B>: users at the same time, or <B>new users per second or per minute</B> (arrival rate). For users at the
            same time, give the <B>maximum users (VUs)</B> and the <B>ramp-up</B>: either users per second or per minute (5 per second:
            100 users are all running after 20 s) or a time in minutes (100 users over 2 minutes: 1QA starts about 0.8 users per
            second). During the <B>steady</B> minutes every user repeats its script; the <B>ramp-down</B> stops them the same two ways.
            For an arrival rate, set ramp-up, steady time and ramp-down. Or use your own stages. Templates fill these in: Smoke, Load, Stress (a staircase), Spike, Soak. The chart
            shows every scenario’s users over time; the test lasts as long as its stages.
          </li>
          <li>
            <B>Goals</B>: 95 % of requests faster than, errors under, and goals for single transactions (“Buyers › Sign in”).
          </li>
        </Bullets>
        <H>Test data files: a row per journey</H>
        <P>
          When every user needs its own values (for example 50 different sign-ins), use a <B>test data file</B>: a CSV or Excel
          (.xlsx) file whose first row names the columns and every row under it is one journey. Open the script and press{' '}
          <B>Download test data template (CSV)</B>: its columns are the test data the script uses (what you made test data in
          the requests), with an example row. Fill in one row per journey and upload it on the <B>Scripts</B> tab under{' '}
          <B>Test data files</B>, or straight from a scenario. Mark columns such as PASSWORD as <B>secret</B>: they are stored
          encrypted, masked in check results and never shown again.
        </P>
        <P>
          On the performance test, choose the file for a scenario under <B>Test data</B>. Each journey takes the next row (the
          first journey row 1, the second row 2, over all load agents). Choose what happens when every row has been used:{' '}
          <B>Start again at row 1</B>, or <B>Stop sending</B> (each row is used once; the report says when the rows ran out).
          Values the file doesn’t have come from the project’s test data. <B>Check the script</B> can use row 1 of a file.
          Load agents need version 1.2 or newer for test data files.
        </P>
        <H>Uploading images and other files</H>
        <P>
          When a journey uploads a file (a profile photo, a document), put the file under <B>Test files</B> on the <B>Scripts</B>{' '}
          tab: up to 10 MB each, 200 per project. Browsers don’t record the file itself, so a recorded upload names the file it
          sent; upload a file with that name, or choose another one. In the script editor, open the request and pick the body{' '}
          <B>Upload form (files and fields)</B> for a form with text and file fields, or <B>A file (the whole body)</B> when the
          request sends just the file. For each file field choose one of the test files, or <B>A different file per journey</B>{' '}
          and write <C>{'{{IMAGE}}'}</C>: list a file name per row in a data file column called IMAGE (cat.png, dog.png…), so the
          users don’t all upload the same picture. Check results show which file was sent and its size. Load agents need version
          1.3 or newer for test files.
        </P>
        <P>
          Many files, larger ones, or files that should stay out of 1QA: keep them on the load agent machines instead. Start each
          agent with <C>--files-dir D:\loadtest</C> (only files in that folder can ever be sent). In the file field choose{' '}
          <B>On the load agent (its files folder)</B> and write the path inside the folder, like <C>{'photos/{{IMAGE}}'}</C>, with
          the file names in a data file column. Every agent of the run needs the same files: before the load starts, each agent
          checks that it has all of them and names the missing ones. Each different file is kept in the agent’s memory once, so
          let rows repeat file names or spread a very large set over more agents. Load agents need version 1.4 or newer for this.
        </P>
        <H>4. Check, then load</H>
        <P>
          Press <B>Run</B> on the test. Start with <B>Check the scripts (1 user)</B>: each scenario runs once and every step should
          answer as recorded. Then press <B>Start load</B>; the dialog lets you change the users and times for this run only. The dialog shows how many users the free agents can carry and whether you may start
          that much load. While it runs you see users, requests per second, response times and errors live; <B>Stop the load</B> ends it at once.
        </P>
        <Table
          head={['Who', 'May start']}
          rows={[
            ['Project Member', 'Up to 500 users for 30 minutes, within what the load agents can carry'],
            ['Project Admin', 'Up to the project’s load limits (Project settings), also above the agents’ estimate'],
          ]}
        />
        <H>Reading the result</H>
        <Bullets>
          <li>
            <B>Passed</B>: every goal met. <B>Goal missed</B>: the report says which and by how much.
          </li>
          <li>
            <B>Not trustworthy</B>: the load agent itself was at its limit, or planned requests were not sent. Use more or bigger agents.
          </li>
          <li>
            <B>Partial result</B>: goals met, but an agent was lost during the run.
          </li>
          <li>
            <B>Results by scenario</B>: open a scenario to see its actions (the steps of the journey), and an action to see its
            requests, with count, requests per second, median, p95, p99, slowest and errors.
          </li>
          <li>
            <B>Export as PDF</B> saves the whole report with every part opened (choose “Save as PDF” in the print window). A shared
            link shows the same report; it leaves out the request and response details of check runs.
          </li>
          <li>
            <B>Findings and suggestions</B>: open each one to read it. What the run says and what to do next, worked out from its errors and numbers. For
            example, errors from the very first users point at the script or its test data (check the script), errors that start
            at some number of users show where the app gives way, a jump in response times while requests per second stay flat
            shows the app’s throughput limit, 401 answers that start after some minutes mean the sign-in expires. They are a
            starting point: confirm with the app’s logs and monitoring.
          </li>
          <li>
            <B>Errors</B> are named in words (for example <B>Not signed in (401)</B>, <B>Service unavailable (503)</B>,{' '}
            <B>Timed out</B>), with what each means, what to do, who most likely has to act (the app, the script or test data,
            the network, the test setup) and the requests that had it most.
          </li>
        </Bullets>
        <Note>Goals are judged on the steady part of the run, after the ramp-up, so a slow first minute doesn’t fail the test. Tests may only load the project’s own address and the hosts a Project Admin allows.</Note>
      </>
    ),
  },
  {
    id: 'security',
    title: 'Security scans: find vulnerabilities automatically',
    summary: 'Point a web project at your staging app, confirm you are authorized to test it, and run an automated penetration test that reports vulnerabilities with a fix for each.',
    keywords: 'security scan penetration pentest vulnerability owasp cwe sql injection xss ssrf authentication authorization exploit code audit source code repository github gitlab access token findings remediation risk grade quick deep scope authorized hosts production rules of engagement',
    roles: TESTERS,
    group: 'Run and review',
    body: (
      <>
        <P>
          A <B>security scan</B> is an automated penetration test of a web app: it explores the app, probes for vulnerabilities, and
          for a <B>Deep scan</B> tries to prove each one with a real (safe) attack. The report gives an overall grade and, for every
          finding, what it is in plain words, the evidence, and how to fix it — so both a manager and an engineer can read it.
        </P>
        <H>1. Create a security project and set the scope</H>
        <P>
          Make a <B>Security</B> project (New project › Security) for the app, then add a scan on its <B>Security tests</B> tab.
          The scan page shows <B>Security scope</B>: list the
          app’s <B>authorized hosts</B> (use staging or test hosts; a leading <C>*.</C> covers sub-domains), add any{' '}
          <B>rules of engagement</B>, and tick <B>I am authorized to test these hosts</B>. A Project Admin sets this, and{' '}
          <B>no scan runs until it is confirmed</B>. Scanning runs real requests and can change data, so production is blocked unless
          a Project Admin deliberately allows it. Only confirm authorization for apps your organization owns or has written permission
          to test.
        </P>
        <H>2. Run a scan</H>
        <P>
          Press <B>Run scan</B> and pick an <B>engine</B>. <B>Black-box pentest</B> (the default) tests the app from its public
          address like an outside attacker — no source code needed — and uses the project’s <B>test data</B> (for example a test
          login) to reach and probe authenticated pages; it needs an <B>Anthropic</B> AI model. <B>Code-level security audit</B> also
          reads the app’s source code for deeper analysis: pick it and connect the app’s <B>code repository</B> right there (or on
          the scan page / project settings) — choose <B>GitHub</B>, <B>GitLab</B> (also self-hosted) or another Git server, paste the
          repository’s https address, optionally a branch, and for a private repository a <B>read-only access token</B> (GitHub:
          fine-grained token with read “Contents”; GitLab: <C>read_repository</C>). 1QA checks it can read the repository before
          saving, stores the token encrypted and never shows it again; a Project Admin connects it. Then pick the depth:{' '}
          <B>Quick</B> (reconnaissance and safe checks only) or <B>Deep</B> (a full autonomous pentest with proof-of-concept
          exploitation; it can take an hour or more). Connect an AI model first (see <See id="ai-models">AI models</See>).
          To test behind a login, add the credentials as <B>test data</B> on the project (mark the password <B>secret</B> so it is
          masked in the report). Scans run on the 1QA security runner — nothing to download.
        </P>
        <H>3. Read the report</H>
        <P>
          The report opens with an <B>A–F grade</B>, a headline anyone can read, and a count of issues by severity. Below it, every
          finding expands to its category and CWE, the evidence (what the scanner did and what came back), and a remediation. Any
          secret test data is masked. Open a scan again any time from the security test’s <B>Scans</B> list.
        </P>
        <Note>Security scans run on their own and only against the authorized hosts above — never other sites.</Note>
      </>
    ),
  },
  {
    id: 'device-agent',
    title: 'Connecting your own phone (Device Agent)',
    summary: 'Run Android tests on a real phone plugged into your computer, through the 1QA Device Agent.',
    keywords: 'device agent real phone physical device usb debugging adb connect own phone laptop computer local android hardware token',
    roles: TESTERS,
    group: 'Settings and admin',
    body: (
      <>
        <P>
          Emulators on the 1QA server are good for most tests. To test on a real phone, plug it into your own computer and run the{' '}
          <B>1QA Device Agent</B> there. The phone then shows up in 1QA under <To to="/helpers/devices">Helper services › Devices</To> and in the <B>Device</B>{' '}
          picker next to Run. Tests still run from 1QA, with the same AI model, reports, screenshots and live view.
        </P>
        <Note title="How it works">
          The agent only makes outgoing HTTPS connections to 1QA: it reports its phones and asks for work. Nothing on the network has to
          reach your computer, so it works behind a firewall, on Wi-Fi and over VPN. The agent never sees 1QA’s database or other teams’
          data.
        </Note>

        <H>What you need</H>
        <Bullets>
          <li>A Windows, macOS or Linux computer you can leave on while tests run.</li>
          <li>
            <B>Node.js 22</B> or newer (<C>node --version</C>).
          </li>
          <li>
            <B>Android platform-tools</B> (adb): install them from developer.android.com/tools/releases/platform-tools, or with Android
            Studio, and make <C>adb</C> available on the PATH (<C>adb version</C>).
          </li>
          <li>An Android phone and its USB cable. iPhones are not supported yet.</li>
          <li>
            Project Admin or Project Member role (View Only users cannot connect devices).
          </li>
        </Bullets>

        <H>1. Create the agent in 1QA</H>
        <Steps>
          <li>
            Open <To to="/helpers/devices">Helper services › Devices</To> › <B>Connect a device</B>.
          </li>
          <li>Name the computer so others know whose phones they are, e.g. “Dana’s laptop” or “QA lab Mac”, and press Continue.</li>
          <li>
            The next screen shows a download link and a ready-made command with the agent’s token. <B>Copy the command now</B>: the token
            is shown only once. Keep it to yourself; anyone with it can run tests on your phones.
          </li>
        </Steps>

        <H>2. Start the agent on your computer</H>
        <Steps>
          <li>
            Download the agent zip, e.g. <B>1qa-device-agent-1.0.0.7.zip</B> (<B>Download agent</B> at the top of the Devices page; <B>Before you start</B> there lists what the computer and phone need) and unzip it
            somewhere you will keep it. The page shows the latest build, e.g. “1.0.0 build 7”; a computer running an older one gets an{' '}
            <B>Update available</B> badge: unzip the new build over the old folder, run <C>npm install</C> and start it again.
          </li>
          <li>
            Open a terminal in the <C>1qa-device-agent</C> folder and run <C>npm install</C> once. It installs the device control the
            agent uses.
          </li>
          <li>Paste and run the command from step 1:</li>
        </Steps>
        <Pre>{`node 1qa-device-agent.mjs --server https://<your 1QA address> --token 1qa_da_…`}</Pre>
        <P>
          It answers “Connected to 1QA … as "Dana’s laptop"”. <B>Leave the window open</B> while tests run; <C>Ctrl+C</C> disconnects. To
          start it again later, run the same command. If adb is not on the PATH, add <C>--adb &lt;path to adb&gt;</C>.
        </P>

        <H>3. Prepare the phone (once)</H>
        <Steps>
          <li>
            On the phone, open <B>Settings › About phone</B> and tap <B>Build number</B> seven times to turn on Developer options.
          </li>
          <li>
            In <B>Settings › System › Developer options</B>, turn on <B>USB debugging</B>.
          </li>
          <li>
            Plug the phone into the computer, unlock it and tap <B>Allow</B> when it asks to allow USB debugging (tick “Always allow from
            this computer”).
          </li>
          <li>
            The agent window lists the phone as ready, and the Devices page shows it as <B>Ready</B>.
          </li>
        </Steps>
        <Tip>
          Several phones on one computer work: each one appears separately. Keep phones unlocked and charging, and turn off screen lock
          or set a long screen timeout so tests are not interrupted.
        </Tip>

        <H>4. Run tests on the phone</H>
        <Steps>
          <li>Open an Android project and its tests, group or flow.</li>
          <li>
            In the <B>Device</B> picker next to Run, choose the phone under <B>Connected phones</B>, e.g. “Pixel 7 · Android 15 · Dana’s
            laptop”.
          </li>
          <li>
            Press <B>Run</B>. Watch it with <B>Watch live</B> if you like; the report shows the phone and its log as for emulators.
          </li>
        </Steps>
        <P>For each test, 1QA:</P>
        <Bullets>
          <li>
            installs the chosen app build on the phone (downloaded once and cached on your computer), or uses the app already on the phone
            when the project has no builds;
          </li>
          <li>clears that app’s data before the test, so every test starts signed out;</li>
          <li>runs one test at a time on each phone (a phone in use shows as Busy);</li>
          <li>attaches the app’s own log and the device details to the report.</li>
        </Bullets>
        <Note title="Your phone and your data">
          1QA installs only the project’s app and clears only that app’s data. It does not read your other apps, photos or messages.
          Screenshots for live view and reports show what is on screen during a test, so close private apps before a run. To keep tests
          off the phone for a while, press <B>Pause</B> on the Devices page; <B>Resume</B> brings it back.
        </Note>

        <H>Pause, remove or move the agent</H>
        <Bullets>
          <li>
            <B>Pause</B>: no tests start on its phones; runs that choose them stop with “paused”.
          </li>
          <li>
            <B>Remove</B>: the token stops working at once and the agent on the computer stops. Use it when a computer is lost or no longer
            used. The person who connected it and Project Admins can pause and remove.
          </li>
          <li>To move to another computer, remove the agent and connect the new computer with a new token.</li>
        </Bullets>

        <H>Troubleshooting</H>
        <Table
          head={['You see', 'Do this']}
          rows={[
            ['“adb was not found”', 'Install Android platform-tools and add them to PATH, or start the agent with --adb <path>.'],
            ['“1QA did not accept the token”', 'The agent was removed in 1QA, or the token was mistyped. Connect the computer again for a new token.'],
            ['Phone shows “Allow USB debugging on the phone”', 'Unlock the phone and tap Allow on the USB debugging prompt. Replug the cable if no prompt appears.'],
            ['No phone listed', 'Check the cable (some only charge), that USB debugging is on, and that adb devices lists the phone.'],
            ['The phone keeps asking to allow USB debugging, or drops for a moment', 'Two different adb versions on the computer restart each other (the agent window says so). Close other Android tools, or start the agent with --adb pointing at the adb you use. Tick “Always allow from this computer” on the phone.'],
            ['Run ends with “… is offline”', 'The agent is not running or the computer is asleep. Start it again and keep the computer awake.'],
            ['Run ends with “phone stayed busy”', 'Another run is using that phone. Wait, or choose another device.'],
            ['“The app … is not installed”', 'Upload a build in the project’s Builds tab, or install the app on the phone yourself.'],
            ['Tests are slow', 'Real phones over Wi-Fi to 1QA are slower than emulators next to it; a wired network or the office network helps.'],
          ]}
        />
      </>
    ),
  },
  {
    id: 'team',
    title: 'Team and invitations',
    summary: 'Invite Project Members and View Only users, and give members their projects.',
    keywords: 'members invite invitation team assign projects remove role change organization switch',
    roles: ['PROJECT_ADMIN'],
    group: 'Settings and admin',
    body: (
      <>
        <Steps>
          <li>
            On <To to="/members">Members</To>, invite someone by email as <B>Project Member</B> or <B>View Only</B>. For members, pick the
            projects they will work in; they are added when they accept.
          </li>
          <li>
            Later, add or remove people from a project on its <B>Team</B> tab.
          </li>
          <li>Change a person’s role or remove them from the organization on Members. An organization always keeps at least one Project Admin.</li>
        </Steps>
        <Note>
          Members only see the projects they are assigned to. View Only users only see reports shared with them. If you belong to more
          than one organization, switch at the top of the sidebar.
        </Note>
      </>
    ),
  },
  {
    id: 'administration',
    title: 'Administration (Admins)',
    summary: 'Users, Project Admin invitations, the organizations overview and the audit dashboard.',
    keywords: 'admin administration users audit log dashboard csv export invitations organizations overview security',
    roles: ['ADMIN'],
    group: 'Settings and admin',
    body: (
      <>
        <Table
          head={['Page', 'What it is for']}
          rows={[
            [<To key="a" to="/admin/audit">Audit dashboard</To>, 'Activity at a glance: sign-ins, changes and runs by area, the busiest people and organizations, a weekday × hour heat map, a “needs attention” list (failed sign-ins, access changes, public links, deletions) and the full log with filters and CSV export.'],
            [<To key="o" to="/admin/organizations">Organizations</To>, 'Every organization with its owner and size. Admins never see what is inside.'],
            [<To key="u" to="/admin/users">Users</To>, 'Everyone on the platform: roles, status, and access.'],
            [<To key="i" to="/admin/invitations">Invitations</To>, 'Invite Project Admins, who then create their organization.'],
          ]}
        />
      </>
    ),
  },
  {
    id: 'troubleshooting',
    title: 'Troubleshooting',
    summary: 'Common messages and what to do about them.',
    keywords: 'problems errors fix help faq not working failed missing why',
    roles: ALL,
    group: 'Help',
    body: (
      <Table
        head={['You see', 'Do this']}
        rows={[
          ['“No AI model is connected…”', 'A Project Admin adds one on AI models, for the organization or the project.'],
          ['“{{NAME}} is not in … test data”', 'Add NAME on Test data (in the collection you run with, or the shared values), or write a fallback: {{NAME|value}}.'],
          ['A web address is refused', 'Use https://. Plain http:// works only for localhost.'],
          ['A step fails although the app looks right', 'Open the trace and the screenshot of that step. Make the step more specific (the exact text in quotes), add a Check before it so the page has loaded, or raise “Retry failed steps”.'],
          ['A test passes, then fails', 'Look for data that works only once (use {{$randomEmail}} or {{$uuid}}) and leftovers from earlier runs.'],
          ['Runs stay queued, then end with “No … runner was connected”', 'The runner for that kind of project is not running. Ask your administrator.'],
          ['“The recorder is not installed”', 'Install it from Helper services › Browser recorder, then reload the 1QA page.'],
          ['A colleague cannot see a project', 'A Project Admin assigns them on the project’s Team tab.'],
          ['A View Only user cannot see a report', 'Share that report with them.'],
          ['An APK upload is refused', 'It must be the project’s app (same package name), an .apk, and within the size limit.'],
        ]}
      />
    ),
  },
];

/** The sections in the order a role should read them first. */
export const QUICK_START: Record<GuideRole, { intro: string; sections: string[] }> = {
  PROJECT_ADMIN: {
    intro: 'Set up your organization, connect a model, create a project and invite your team.',
    sections: ['getting-started', 'ai-models', 'projects', 'team', 'writing-tests', 'automation', 'notifications'],
  },
  MEMBER: {
    intro: 'Write your first test, run it and read the report.',
    sections: ['writing-tests', 'recorder', 'test-data', 'running', 'reports', 'device-agent'],
  },
  VIEWER: {
    intro: 'Open the reports shared with you and comment on them.',
    sections: ['reports', 'roles'],
  },
  ADMIN: {
    intro: 'Invite Project Admins and keep an eye on activity.',
    sections: ['getting-started', 'administration', 'roles'],
  },
};

const norm = (s: string) => s.toLowerCase().normalize('NFKD').replace(/[̀-ͯ]/g, '');

/** Sections whose title, summary or keywords contain every word of the query. */
export function searchGuide(sections: GuideSection[], query: string): GuideSection[] {
  const words = norm(query).split(/\s+/).filter(Boolean);
  if (!words.length) return sections;
  return sections.filter((s) => {
    const text = norm(`${s.title} ${s.summary} ${s.keywords} ${s.group}`);
    return words.every((w) => text.includes(w));
  });
}
